import i18next from 'i18next';
import {Button, Text, createStack} from 'modern-famly';
import React from 'react';
import styled from 'styled-components';

import Link from 'web-app/react/components/link/link';
import {PortalModal} from 'web-app/react/components/modals/modals';
import ModalHeader from 'web-app/react/components/modals/components/new-modal-header';
import {PortalRootId} from 'web-app/react/components/portals/constants';
import {Flex} from 'web-app/react/components/layout/layout';
import {s0, s4} from 'web-app/styleguide/spacing';
import {exhaustiveCheck} from 'web-app/util/typescript';

const markets = ['uk', 'dach', 'us', 'dk', 'other'] as const;
type Market = typeof markets[number];

export const LoginTermsAndConditions = () => {
    const [showModal, setShowModal] = React.useState(false);
    return (
        <>
            <Link cursor="pointer" onClick={() => setShowModal(true)}>
                <Text variant="body" color="n300">
                    {i18next.t('login.termsAndPolicies')}
                </Text>
            </Link>
            {showModal ? (
                <PortalModal onCloseModal={() => setShowModal(false)} rootId={PortalRootId.contractPortalRoot}>
                    <Flex direction="column" fullHeight>
                        <ModalHeader header={i18next.t('menu.termsAndPolicies.header')} />
                        <Flex grow={1} padding={[s0, s4, s4, s4]} direction="column" justify="space-between">
                            <div>
                                <Text variant="body" mb={2}>
                                    {i18next.t('menu.termsAndPolicies.description')}
                                </Text>
                                {markets.map(market => (
                                    <TermsLinkContainer>
                                        <Link href={getTermsUrl(market)} target="_blank">
                                            {getMarketName(market)}
                                        </Link>
                                    </TermsLinkContainer>
                                ))}
                            </div>
                            <Flex justify="flex-end" marginTop={s4}>
                                <Button onClick={() => setShowModal(false)} text={i18next.t('close')} />
                            </Flex>
                        </Flex>
                    </Flex>
                </PortalModal>
            ) : null}
        </>
    );
};

const TermsLinkContainer = styled(
    createStack({
        height: '44px',
        boxSizing: 'border-box',
        alignItems: 'center',
    }),
)`
    border-bottom: 1px solid ${props => props.theme.mf.colorPalette.n100};

    &:last-child {
        border-bottom: none;
    }
`;

/**
 * The logic here is ported from famlyapi:
 *
 * https://github.com/famly/nuclearfamly/blob/master/famlybiz/src/main/scala/co/famly/core/TermsAndConditions.scala
 */
const getTermsUrl = (market: Market) => {
    switch (market) {
        case 'uk':
            return 'https://www.famly.co/security-and-legal#all-terms';
        case 'us':
            return 'https://www.famly.co/us-support#all-terms';
        case 'dk':
            return 'https://www.famly.co/da-om-famly#alle-betingelser';
        case 'dach':
            return 'https://www.famly.co/de-sicherheit#alle-bedingungen';
        case 'other':
            return 'https://www.famly.co/security-and-legal#all-terms';
        default:
            exhaustiveCheck(market);
            return 'https://www.famly.co/security-and-legal#all-terms';
    }
};

const getMarketName = (market: Market) => {
    switch (market) {
        case 'uk':
            return i18next.t('menu.termsAndPolicies.uk');
        case 'us':
            return i18next.t('menu.termsAndPolicies.us');
        case 'dach':
            return i18next.t('menu.termsAndPolicies.dach');
        case 'dk':
            return i18next.t('menu.termsAndPolicies.dk');
        case 'other':
            return i18next.t('menu.termsAndPolicies.other');
        default:
            exhaustiveCheck(market);
            return i18next.t('menu.termsAndPolicies.other');
    }
};
